import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Search, X } from "lucide-react";
import "./HeaderGlobalSearch.css";

const SEARCH_ITEMS = [
  // Overview
  {
    id: "dashboard",
    title: "Dashboard",
    description: "Overview and portal summary",
    to: "/dashboard",
    slug: "dashboard",
    category: "Overview",
    keywords: ["home", "overview"],
  },
  {
    id: "notifications",
    title: "Notifications",
    description: "Alerts and updates",
    to: "/notifications",
    category: "Overview",
    keywords: ["notification", "alerts"],
  },
  {
    id: "approvals",
    title: "Approval Requests",
    description: "Pending approvals",
    to: "/approvals",
    category: "Overview",
    keywords: ["approval", "request"],
  },

  // Tenants
  {
    id: "tenant",
    title: "Tenant Users",
    description: "Onboard and manage tenants",
    to: "/tenant",
    slug: "onboarding-tenant",
    category: "Tenants",
    keywords: ["tenant", "merchant", "customer", "list"],
  },
  {
    id: "tenant-add",
    title: "Add Tenant",
    description: "Create a new tenant account",
    to: "/tenant/add",
    slug: "onboarding-tenant",
    access: "canCreate",
    category: "Tenants",
    keywords: ["tenant", "create", "add", "new"],
  },

  // Payments & billing
  {
    id: "transactions",
    title: "Transactions",
    description: "Payments and billing activity",
    to: "/transactions",
    slug: "transaction",
    category: "Payments",
    keywords: ["payment", "billing", "qbot", "tripola", "kyc"],
  },
  {
    id: "paycentral-transactions",
    title: "Paycentral Transactions",
    description: "Paycentral payment activity",
    to: "/paycentral-transactions",
    slug: "paycentral-transaction",
    category: "Payments",
    keywords: ["paycentral", "payment", "transaction"],
  },
  {
    id: "invoices",
    title: "Automated Invoice",
    description: "Invoice generation and schedules",
    to: "/invoices",
    slug: "paycentral-invoice",
    category: "Payments",
    keywords: ["invoice", "billing", "automated"],
  },
  {
    id: "reports",
    title: "Reports",
    description: "Analytics and performance reports",
    to: "/reports",
    slug: "reports",
    category: "Payments",
    keywords: ["analytics", "kpi", "report"],
  },

  // Plans
  {
    id: "plan-management",
    title: "Plan Management",
    description: "Subscription plans across portals",
    to: "/plan-management",
    slug: "plan-management",
    category: "Plans",
    keywords: ["plan", "pricing", "subscription", "qbot", "kyc", "tripola"],
  },
  {
    id: "plan-features",
    title: "Plan Features",
    description: "Feature pricing and add-ons",
    to: "/plan-feature-management",
    slug: "plan-features",
    category: "Plans",
    keywords: ["feature", "addon", "add-on", "plan feature"],
  },

  // KYC
  {
    id: "country-document",
    title: "KYC Country Document",
    description: "Country and document mapping",
    to: "/country-document",
    slug: "kyc-country-document",
    category: "KYC",
    keywords: ["kyc", "country", "document"],
  },
  {
    id: "documentation-type",
    title: "KYC Country Document Type",
    description: "Document type definitions",
    to: "/documentation-type",
    slug: "kyc-document-type",
    category: "KYC",
    keywords: ["kyc", "document type", "provider", "sumsub"],
  },

  // Administration
  {
    id: "users",
    title: "User Management",
    description: "Master portal users",
    to: "/users",
    slug: "users",
    category: "Administration",
    keywords: ["user", "staff", "admin", "master user"],
  },
  {
    id: "roles",
    title: "Role Management",
    description: "Permissions and roles",
    to: "/role-manager",
    slug: "roles",
    category: "Administration",
    keywords: ["role", "permission", "access"],
  },
  {
    id: "support-ticket",
    title: "Ticketing System",
    description: "Support tickets and requests",
    to: "/support-ticket",
    slug: "support-ticket",
    category: "Administration",
    keywords: ["ticket", "support", "help"],
  },
  {
    id: "audit-trail",
    title: "Audit Trail",
    description: "System activity history",
    to: "/audit-trail",
    slug: "audit-trail",
    category: "Administration",
    keywords: ["audit", "logs", "history"],
  },
  {
    id: "mail-logs",
    title: "Email Logs",
    description: "Outbound email history",
    to: "/mail-logs",
    slug: "mail-logs",
    category: "Administration",
    keywords: ["email", "mail", "logs"],
  },
  {
    id: "master-tenant-logs",
    title: "Master Tenant Logs",
    description: "Tenant change history",
    to: "/master-tenant-logs",
    slug: "master-tenant-logs",
    category: "Administration",
    keywords: ["tenant logs", "history", "master logs"],
  },

  // Account & content
  {
    id: "profile",
    title: "Profile",
    description: "Your account details",
    to: "/profile",
    category: "Account",
    keywords: ["account", "me", "profile"],
  },
  {
    id: "change-password",
    title: "Change Password",
    description: "Update your login password",
    to: "/change-password",
    category: "Account",
    keywords: ["password", "security", "credentials"],
  },
  {
    id: "settings",
    title: "Settings",
    description: "Portal configuration",
    to: "/settings",
    category: "Account",
    keywords: ["config", "preferences", "settings"],
  },
  {
    id: "login-sessions",
    title: "Login Sessions",
    description: "Active and past sessions",
    to: "/login-sessions",
    category: "Account",
    keywords: ["session", "login", "device"],
  },
  {
    id: "whitelist",
    title: "Whitelist IPs",
    description: "Allowed IP addresses",
    to: "/whitelist-ips",
    slug: "ip-whitelist",
    category: "Account",
    keywords: ["ip", "whitelist", "security"],
  },
  {
    id: "templates",
    title: "Email Templates",
    description: "Outbound email templates",
    to: "/templates",
    slug: "templates",
    category: "Account",
    keywords: ["template", "email"],
  },
  {
    id: "terms",
    title: "Terms & Conditions",
    description: "Legal terms management",
    to: "/terms-conditions",
    category: "Account",
    keywords: ["terms", "legal", "policy", "tnc"],
  },
  {
    id: "knowledge-base",
    title: "Knowledge Base",
    description: "Knowledge base videos related all modules",
    to: "/knowledge-base",
    category: "Knowledge Base",
    keywords: ["videos","knowledge"],
  },
];

const QUICK_SEARCH_IDS = [
  "tenant",
  "tenant-add",
  "transactions",
  "approvals",
  "plan-management",
  "users",
  "support-ticket",
  "reports",
  "knowledge-base"
];

const normalize = (value = "") => value.toLowerCase().trim();

const HeaderGlobalSearch = ({ permission = [] }) => {
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const hasAccess = (item) => {
    if (!item.slug) return true;
    const module = permission[0]?.role_permissions?.find(
      (m) => m?.module?.slug === item.slug
    );
    if (!module) return false;
    const flag = item.access || "canView";
    return Boolean(module[flag]);
  };

  const availableItems = useMemo(
    () => SEARCH_ITEMS.filter((item) => hasAccess(item)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [permission]
  );

  const quickItems = useMemo(() => {
    const byId = new Map(availableItems.map((item) => [item.id, item]));
    return QUICK_SEARCH_IDS.map((id) => byId.get(id)).filter(Boolean);
  }, [availableItems]);

  const isBrowsing = !normalize(query);

  const results = useMemo(() => {
    const q = normalize(query);
    if (!q) return availableItems.slice(0, 10);

    return availableItems
      .map((item) => {
        const haystack = [
          item.title,
          item.description,
          item.category,
          item.to,
          ...(item.keywords || []),
        ]
          .join(" ")
          .toLowerCase();
        const score =
          (normalize(item.title).startsWith(q) ? 4 : 0) +
          (normalize(item.title).includes(q) ? 3 : 0) +
          (normalize(item.to).includes(q) ? 2 : 0) +
          (haystack.includes(q) ? 1 : 0);
        return { item, score };
      })
      .filter((row) => row.score > 0)
      .sort((a, b) => b.score - a.score || a.item.title.localeCompare(b.item.title))
      .slice(0, 20)
      .map((row) => row.item);
  }, [availableItems, query]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, open]);

  useEffect(() => {
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      const isShortcut =
        (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k";
      if (!isShortcut) return;
      event.preventDefault();
      inputRef.current?.focus();
      setOpen(true);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const goTo = (item) => {
    if (!item?.to) return;
    navigate(item.to);
    setQuery("");
    setOpen(false);
    inputRef.current?.blur();
  };

  const applyQuickFilter = (item) => {
    const seed = item?.keywords?.[0] || item?.title || "";
    setQuery(seed);
    setOpen(true);
    inputRef.current?.focus();
  };

  const onKeyDown = (event) => {
    if (event.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
      return;
    }

    if (!open || results.length === 0) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((prev) => (prev + 1) % results.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((prev) => (prev - 1 + results.length) % results.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      goTo(results[activeIndex]);
    }
  };

  return (
    <div className="mp-global-search" ref={rootRef}>
      <label className="mp-global-search-field" htmlFor="mp-global-search-input">
        <Search size={16} strokeWidth={2} aria-hidden />
        <input
          id="mp-global-search-input"
          ref={inputRef}
          type="search"
          autoComplete="off"
          placeholder="Search pages…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls="mp-global-search-results"
        />
        {query ? (
          <button
            type="button"
            className="mp-global-search-clear"
            aria-label="Clear search"
            onClick={() => {
              setQuery("");
              inputRef.current?.focus();
            }}
          >
            <X size={14} strokeWidth={2} />
          </button>
        ) : (
          <kbd className="mp-global-search-kbd">Ctrl K</kbd>
        )}
      </label>

      {open ? (
        <div
          id="mp-global-search-results"
          className="mp-global-search-panel"
          role="listbox"
        >
          {isBrowsing && quickItems.length > 0 ? (
            <div className="mp-global-search-quick">
              <div className="mp-global-search-section-label">Quick search</div>
              <div className="mp-global-search-chips">
                {quickItems.map((item) => (
                  <button
                    key={`quick-${item.id}`}
                    type="button"
                    className="mp-global-search-chip"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => goTo(item)}
                    title={item.description}
                  >
                    {item.title}
                  </button>
                ))}
              </div>
              <div className="mp-global-search-chip-hint">
                Or click a chip to open that page instantly
              </div>
            </div>
          ) : null}

          {results.length === 0 ? (
            <div className="mp-global-search-empty">
              No pages match “{query.trim()}”
              {quickItems.length > 0 ? (
                <div className="mp-global-search-empty-quick">
                  <span>Try quick search:</span>
                  <div className="mp-global-search-chips">
                    {quickItems.slice(0, 4).map((item) => (
                      <button
                        key={`empty-quick-${item.id}`}
                        type="button"
                        className="mp-global-search-chip"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => applyQuickFilter(item)}
                      >
                        {item.title}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          ) : (
            <>
              <div className="mp-global-search-section-label">
                {isBrowsing ? "All pages" : "Results"}
              </div>
              {results.map((item, index) => (
                <button
                  key={item.id}
                  type="button"
                  role="option"
                  aria-selected={index === activeIndex}
                  className={`mp-global-search-item${
                    index === activeIndex ? " is-active" : ""
                  }`}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => goTo(item)}
                >
                  <span className="mp-global-search-item-copy">
                    <strong>{item.title}</strong>
                    <small>
                      {item.category ? `${item.category} · ` : ""}
                      {item.description}
                    </small>
                  </span>
                  <ArrowRight size={15} strokeWidth={2} aria-hidden />
                </button>
              ))}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default HeaderGlobalSearch;
